import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { getRoomHistory } from '../../utils/api';

// Hook for responsive detection
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
      setIsTablet(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { isMobile, isTablet };
};

// Metrics configuration
const METRICS = [
  { key: 'temp1', label: 'Temperatura 1', unit: '°C', category: 'temperature' },
  { key: 'temp2', label: 'Temperatura 2', unit: '°C', category: 'temperature' },
  { key: 'temp_do1', label: 'Temp Sensor DO1', unit: '°C', category: 'temperature' },
  { key: 'temp_do2', label: 'Temp Sensor DO2', unit: '°C', category: 'temperature' },
  { key: 'do1_mgL', label: 'Oxígeno Disuelto 1', unit: 'mg/L', category: 'oxygen' },
  { key: 'do2_mgL', label: 'Oxígeno Disuelto 2', unit: 'mg/L', category: 'oxygen' },
  { key: 'sat1_pct', label: 'Saturación 1', unit: '%', category: 'saturation' },
  { key: 'sat2_pct', label: 'Saturación 2', unit: '%', category: 'saturation' },
];

// Time ranges (Grafana-style)
const TIME_RANGES = [
  { key: '5m', label: '5 min' },
  { key: '15m', label: '15 min' },
  { key: '1h', label: '1 hora' },
  { key: '6h', label: '6 horas' },
  { key: '24h', label: '24 horas' },
  { key: '3d', label: '3 días' },
  { key: '7d', label: '7 días' },
  { key: '30d', label: '30 días' },
  { key: '90d', label: '90 días' },
];

// Threshold for switching to peaks-only mode
const OUT_OF_RANGE_THRESHOLD = 30;

// Find representative peaks from out-of-range events
// Guarantees that the worst high and worst low are always included
const findRepresentativePeaks = (data, thresholds, maxPeaks = 15) => {
  if (!thresholds || !data.length) return new Set();

  // Get all out-of-range indices with their values
  const outOfRangeEvents = [];
  let worstHighEvent = null;
  let worstLowEvent = null;

  data.forEach((d, idx) => {
    const isAboveMax = d.value > thresholds.idealMax;
    const isBelowMin = d.value < thresholds.idealMin;
    if (isAboveMax || isBelowMin) {
      const event = {
        idx,
        value: d.value,
        deviation: isAboveMax
          ? d.value - thresholds.idealMax
          : thresholds.idealMin - d.value,
        type: isAboveMax ? 'high' : 'low',
      };
      outOfRangeEvents.push(event);

      // Track worst high and worst low separately
      if (isAboveMax) {
        if (!worstHighEvent || d.value > worstHighEvent.value) {
          worstHighEvent = event;
        }
      } else {
        if (!worstLowEvent || d.value < worstLowEvent.value) {
          worstLowEvent = event;
        }
      }
    }
  });

  if (outOfRangeEvents.length <= maxPeaks) {
    return new Set(outOfRangeEvents.map((e) => e.idx));
  }

  // Cluster nearby events and pick the most extreme from each cluster
  const clusterGap = Math.max(3, Math.floor(data.length / maxPeaks));
  const peaks = new Set();
  let currentCluster = [];

  outOfRangeEvents.forEach((event, i) => {
    const prevEvent = outOfRangeEvents[i - 1];
    const isNewCluster = !prevEvent || event.idx - prevEvent.idx > clusterGap;

    if (isNewCluster && currentCluster.length > 0) {
      // Pick the most extreme from the previous cluster
      const extreme = currentCluster.reduce((max, e) =>
        e.deviation > max.deviation ? e : max
      );
      peaks.add(extreme.idx);
      currentCluster = [];
    }

    currentCluster.push(event);
  });

  // Handle last cluster
  if (currentCluster.length > 0) {
    const extreme = currentCluster.reduce((max, e) =>
      e.deviation > max.deviation ? e : max
    );
    peaks.add(extreme.idx);
  }

  // ALWAYS include the worst high and worst low (guaranteed visibility)
  if (worstHighEvent) peaks.add(worstHighEvent.idx);
  if (worstLowEvent) peaks.add(worstLowEvent.idx);

  // If still too many, keep the worst high, worst low, and then the most extreme ones
  if (peaks.size > maxPeaks) {
    const guaranteedPeaks = new Set();
    if (worstHighEvent) guaranteedPeaks.add(worstHighEvent.idx);
    if (worstLowEvent) guaranteedPeaks.add(worstLowEvent.idx);

    const remainingSlots = maxPeaks - guaranteedPeaks.size;
    const sortedByDeviation = outOfRangeEvents
      .filter((e) => peaks.has(e.idx) && !guaranteedPeaks.has(e.idx))
      .sort((a, b) => b.deviation - a.deviation)
      .slice(0, remainingSlots);

    sortedByDeviation.forEach((e) => guaranteedPeaks.add(e.idx));
    return guaranteedPeaks;
  }

  return peaks;
};

// Custom dot component - only renders for out-of-range values
const OutOfRangeDot = ({ cx, cy, payload, thresholds, index, representativePeaks, showAllDots }) => {
  if (!thresholds || cx === undefined || cy === undefined) return null;

  const value = payload.value;
  if (value === undefined || value === null) return null;

  // Check if value is out of ideal range
  const isAboveMax = value > thresholds.idealMax;
  const isBelowMin = value < thresholds.idealMin;

  if (!isAboveMax && !isBelowMin) return null;

  // If in peaks mode and this isn't a representative peak, skip it
  if (!showAllDots && representativePeaks && !representativePeaks.has(index)) {
    return null;
  }

  const color = isAboveMax ? '#ef4444' : '#f59e0b'; // red for above, yellow for below
  const isPeak = representativePeaks && representativePeaks.has(index);

  return (
    <circle
      cx={cx}
      cy={cy}
      r={isPeak && !showAllDots ? 7 : 5}
      fill={color}
      stroke="#fff"
      strokeWidth={isPeak && !showAllDots ? 3 : 2}
    />
  );
};

// Custom tooltip
const HistoricalTooltip = ({ active, payload, thresholds, unit }) => {
  if (!active || !payload || !payload.length) return null;

  const data = payload[0].payload;
  const value = data.value;
  const timestamp = new Date(data.timestamp);

  const formatDate = (d) => {
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatTime = (d) => {
    return d.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  let status = 'normal';
  let statusLabel = 'Normal';
  let statusColor = 'var(--accent-green)';

  if (thresholds) {
    if (value > thresholds.idealMax) {
      status = 'high';
      statusLabel = `Sobre máximo ideal (${thresholds.idealMax}${unit})`;
      statusColor = '#ef4444';
    } else if (value < thresholds.idealMin) {
      status = 'low';
      statusLabel = `Bajo mínimo ideal (${thresholds.idealMin}${unit})`;
      statusColor = '#f59e0b';
    }
  }

  return (
    <div className="historical-tooltip">
      <div className="tooltip-date">{formatDate(timestamp)}</div>
      <div className="tooltip-time">{formatTime(timestamp)}</div>
      <div className="tooltip-value" style={{ color: statusColor }}>
        {value?.toFixed(2)} {unit}
      </div>
      {status !== 'normal' && (
        <div className="tooltip-status" style={{ color: statusColor }}>
          {statusLabel}
        </div>
      )}
    </div>
  );
};

// Format timestamp for X axis
const formatXAxis = (timestamp, range) => {
  const d = new Date(timestamp);
  // For short ranges, show time only
  if (['5m', '15m'].includes(range)) {
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  }
  // For medium-short ranges, show time
  if (['1h', '6h'].includes(range)) {
    return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
  }
  // For medium ranges, show day and hour
  if (['24h', '3d'].includes(range)) {
    return `${d.getDate()}/${d.getMonth() + 1} ${d.getHours()}h`;
  }
  // For long ranges, show date only
  return `${d.getDate()}/${d.getMonth() + 1}`;
};

// Get tick interval based on range
const getTickInterval = (dataLength, range) => {
  // Target number of ticks for each range
  const targetTicks = {
    '5m': 5,
    '15m': 5,
    '1h': 6,
    '6h': 6,
    '24h': 8,
    '3d': 6,
    '7d': 7,
    '30d': 6,
    '90d': 6,
  };
  const target = targetTicks[range] || 6;
  return Math.max(1, Math.floor(dataLength / target));
};

// Downsample data for smoother visualization on long ranges
// Uses min-max preservation to keep extreme values visible
const downsampleData = (data, range) => {
  // Only downsample for ranges >= 1h
  if (['5m', '15m'].includes(range) || data.length <= 100) {
    return data;
  }

  // Target points based on range
  const targetPoints = {
    '1h': 60,
    '6h': 72,
    '24h': 96,
    '3d': 72,
    '7d': 84,
    '30d': 90,
    '90d': 90,
  };

  const target = targetPoints[range] || 100;

  if (data.length <= target) {
    return data;
  }

  // Find global extremes to ensure they're always preserved
  let globalMaxIdx = 0;
  let globalMinIdx = 0;
  data.forEach((d, idx) => {
    if (d.value > data[globalMaxIdx].value) globalMaxIdx = idx;
    if (d.value < data[globalMinIdx].value) globalMinIdx = idx;
  });

  const factor = Math.ceil(data.length / target);
  const result = [];
  const addedIndices = new Set();

  for (let i = 0; i < data.length; i += factor) {
    const chunkStart = i;
    const chunkEnd = Math.min(i + factor, data.length);
    const chunk = data.slice(chunkStart, chunkEnd);

    if (chunk.length === 0) continue;

    // Find min and max in this chunk
    let minIdx = 0;
    let maxIdx = 0;
    chunk.forEach((d, idx) => {
      if (d.value < chunk[minIdx].value) minIdx = idx;
      if (d.value > chunk[maxIdx].value) maxIdx = idx;
    });

    const absoluteMinIdx = chunkStart + minIdx;
    const absoluteMaxIdx = chunkStart + maxIdx;

    // Add points in chronological order, avoiding duplicates
    const pointsToAdd = [];

    if (minIdx !== maxIdx) {
      // Add both min and max if they're different points
      if (minIdx < maxIdx) {
        pointsToAdd.push({ idx: absoluteMinIdx, data: chunk[minIdx] });
        pointsToAdd.push({ idx: absoluteMaxIdx, data: chunk[maxIdx] });
      } else {
        pointsToAdd.push({ idx: absoluteMaxIdx, data: chunk[maxIdx] });
        pointsToAdd.push({ idx: absoluteMinIdx, data: chunk[minIdx] });
      }
    } else {
      // Min and max are the same point, just add one
      pointsToAdd.push({ idx: absoluteMinIdx, data: chunk[minIdx] });
    }

    // Add points avoiding duplicates
    pointsToAdd.forEach(({ idx, data: pointData }) => {
      if (!addedIndices.has(idx)) {
        addedIndices.add(idx);
        result.push({
          value: pointData.value,
          timestamp: pointData.timestamp,
          _originalIndex: idx,
        });
      }
    });
  }

  // Ensure global extremes are included
  if (!addedIndices.has(globalMaxIdx)) {
    result.push({
      value: data[globalMaxIdx].value,
      timestamp: data[globalMaxIdx].timestamp,
      _originalIndex: globalMaxIdx,
    });
  }
  if (!addedIndices.has(globalMinIdx)) {
    result.push({
      value: data[globalMinIdx].value,
      timestamp: data[globalMinIdx].timestamp,
      _originalIndex: globalMinIdx,
    });
  }

  // Sort by timestamp to maintain chronological order
  result.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

  return result;
};

/**
 * Historical View Component
 */
export const HistoricalView = ({ roomCode, sensorConfig }) => {
  const [selectedMetric, setSelectedMetric] = useState('temp1');
  const [selectedRange, setSelectedRange] = useState('1h');
  const [data, setData] = useState([]);
  const [thresholds, setThresholds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAllDots, setShowAllDots] = useState(false); // Toggle for dot display mode

  const { isMobile, isTablet } = useIsMobile();
  const currentMetric = METRICS.find((m) => m.key === selectedMetric);

  // Responsive chart settings
  const chartConfig = useMemo(() => ({
    height: isMobile ? 250 : isTablet ? 300 : 350,
    margin: isMobile
      ? { top: 10, right: 10, left: -10, bottom: 5 }
      : { top: 20, right: 30, left: 10, bottom: 10 },
    yAxisWidth: isMobile ? 35 : 50,
    yAxisFontSize: isMobile ? 10 : 13,
    xAxisFontSize: isMobile ? 9 : 11,
    refLineFontSize: isMobile ? 8 : 10,
  }), [isMobile, isTablet]);

  // Get thresholds from local config based on metric category
  const getLocalThresholds = useCallback((metric) => {
    if (!sensorConfig) return null;
    const metricInfo = METRICS.find((m) => m.key === metric);
    if (!metricInfo) return null;

    const categoryConfig = sensorConfig[metricInfo.category];
    if (!categoryConfig) return null;

    return {
      min: categoryConfig.min,
      idealMin: categoryConfig.idealMin,
      idealMax: categoryConfig.idealMax,
      max: categoryConfig.max,
    };
  }, [sensorConfig]);

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!roomCode || !selectedMetric) return;

    setLoading(true);
    setError(null);

    try {
      const result = await getRoomHistory(roomCode, selectedMetric, selectedRange);
      setData(result.data || []);

      // Use server thresholds if available, otherwise use local config
      const serverThresholds = result.thresholds;
      const localThresholds = getLocalThresholds(selectedMetric);
      const finalThresholds = serverThresholds || localThresholds;

      console.log('Historical data response:', {
        dataCount: result.data?.length,
        serverThresholds,
        localThresholds,
        usingThresholds: finalThresholds
      });

      setThresholds(finalThresholds);
    } catch (err) {
      console.error('Error fetching historical data:', err);
      setError('Error al cargar datos históricos');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [roomCode, selectedMetric, selectedRange, getLocalThresholds]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Process data for chart (downsample for long ranges)
  const chartData = React.useMemo(() => {
    return downsampleData(data, selectedRange);
  }, [data, selectedRange]);

  // Calculate tick interval for X axis
  const tickInterval = React.useMemo(() => {
    return getTickInterval(chartData.length, selectedRange);
  }, [chartData.length, selectedRange]);

  // Calculate summary stats
  const summary = React.useMemo(() => {
    if (!data.length || !thresholds) {
      return { aboveMax: 0, belowMin: 0, worstHigh: null, worstLow: null };
    }

    let aboveMax = 0;
    let belowMin = 0;
    let worstHigh = null;
    let worstLow = null;

    data.forEach((d) => {
      if (d.value > thresholds.idealMax) {
        aboveMax++;
        if (worstHigh === null || d.value > worstHigh) {
          worstHigh = d.value;
        }
      } else if (d.value < thresholds.idealMin) {
        belowMin++;
        if (worstLow === null || d.value < worstLow) {
          worstLow = d.value;
        }
      }
    });

    return { aboveMax, belowMin, worstHigh, worstLow };
  }, [data, thresholds]);

  const totalOutOfRange = summary.aboveMax + summary.belowMin;

  // Calculate representative peaks for dense event visualization
  const representativePeaks = useMemo(() => {
    if (!chartData.length || !thresholds) return new Set();
    return findRepresentativePeaks(chartData, thresholds);
  }, [chartData, thresholds]);

  // Determine if we're in dense mode (many out-of-range events)
  const isDenseMode = totalOutOfRange > OUT_OF_RANGE_THRESHOLD;

  // Effective dot display mode
  const effectiveShowAllDots = showAllDots || !isDenseMode;

  return (
    <div className="historical-view fade-in">
      {/* Controls */}
      <div className="historical-controls">
        {/* Metric selector */}
        <div className="historical-control-group">
          <label className="historical-label">Métrica</label>
          <select
            className="historical-select"
            value={selectedMetric}
            onChange={(e) => setSelectedMetric(e.target.value)}
          >
            {METRICS.map((m) => (
              <option key={m.key} value={m.key}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* Range selector */}
        <div className="historical-control-group">
          <label className="historical-label">Rango</label>
          <div className="historical-range-buttons">
            {TIME_RANGES.map((r) => (
              <button
                key={r.key}
                className={`historical-range-btn ${selectedRange === r.key ? 'active' : ''}`}
                onClick={() => setSelectedRange(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Summary - above chart */}
      {!loading && !error && data.length > 0 && (
        <div className="historical-summary">
          <div className="historical-summary-title">Resumen del período</div>
          <div className="historical-summary-content">
            {!thresholds ? (
              <span className="historical-summary-warning">
                No hay umbrales configurados para esta métrica
              </span>
            ) : totalOutOfRange === 0 ? (
              <span className="historical-summary-ok">
                Todos los valores dentro del rango permitido
              </span>
            ) : (
              <>
                <span className="historical-summary-events">
                  {totalOutOfRange} evento{totalOutOfRange !== 1 ? 's' : ''} fuera de rango
                </span>
                {summary.aboveMax > 0 && (
                  <span className="historical-summary-high">
                    {summary.aboveMax} sobre el máximo
                  </span>
                )}
                {summary.belowMin > 0 && (
                  <span className="historical-summary-low">
                    {summary.belowMin} bajo el mínimo
                  </span>
                )}
                {summary.worstHigh !== null && (
                  <span className="historical-summary-worst high">
                    Peor máx: {summary.worstHigh.toFixed(2)} {currentMetric?.unit}
                  </span>
                )}
                {summary.worstLow !== null && (
                  <span className="historical-summary-worst low">
                    Peor mín: {summary.worstLow.toFixed(2)} {currentMetric?.unit}
                  </span>
                )}
                {/* Toggle for dense mode */}
                {isDenseMode && (
                  <button
                    className={`historical-dot-mode-btn ${showAllDots ? 'all' : 'peaks'}`}
                    onClick={() => setShowAllDots(!showAllDots)}
                    title={showAllDots ? 'Mostrando todos los eventos' : 'Mostrando solo picos representativos'}
                  >
                    {showAllDots ? 'Ver solo picos' : `Ver todos (${totalOutOfRange})`}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Chart container */}
      <div className="historical-chart-container">
        {loading && (
          <div className="historical-loading">
            <div className="historical-spinner"></div>
            <span>Cargando datos...</span>
          </div>
        )}

        {error && (
          <div className="historical-error">
            <span>{error}</span>
            <button onClick={fetchData} className="historical-retry-btn">
              Reintentar
            </button>
          </div>
        )}

        {!loading && !error && data.length === 0 && (
          <div className="historical-empty">
            No hay datos para el rango seleccionado
          </div>
        )}

        {!loading && !error && data.length > 0 && (
          <ResponsiveContainer width="100%" height={chartConfig.height}>
            <LineChart data={chartData} margin={chartConfig.margin}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis
                dataKey="timestamp"
                stroke="#64748b"
                fontSize={chartConfig.xAxisFontSize}
                tickFormatter={(t) => formatXAxis(t, selectedRange)}
                tickMargin={isMobile ? 4 : 8}
                interval={tickInterval}
              />
              <YAxis
                stroke="#94a3b8"
                fontSize={chartConfig.yAxisFontSize}
                fontWeight={500}
                domain={['auto', 'auto']}
                width={chartConfig.yAxisWidth}
                tickFormatter={(v) => v.toFixed(1)}
              />
              <Tooltip
                content={
                  <HistoricalTooltip
                    thresholds={thresholds}
                    unit={currentMetric?.unit || ''}
                  />
                }
              />

              {/* Reference lines for ideal thresholds */}
              {thresholds && (
                <>
                  <ReferenceLine
                    y={thresholds.idealMax}
                    stroke="#ef4444"
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    label={isMobile ? null : {
                      value: `Máx: ${thresholds.idealMax}`,
                      position: 'right',
                      fill: '#ef4444',
                      fontSize: chartConfig.refLineFontSize,
                    }}
                  />
                  <ReferenceLine
                    y={thresholds.idealMin}
                    stroke="#f59e0b"
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    label={isMobile ? null : {
                      value: `Mín: ${thresholds.idealMin}`,
                      position: 'right',
                      fill: '#f59e0b',
                      fontSize: chartConfig.refLineFontSize,
                    }}
                  />
                </>
              )}

              {/* Main line */}
              <Line
                type={['5m', '15m'].includes(selectedRange) ? 'monotone' : 'natural'}
                dataKey="value"
                stroke="var(--accent-cyan)"
                strokeWidth={isMobile ? 1.5 : 2}
                dot={(props) => (
                  <OutOfRangeDot
                    {...props}
                    thresholds={thresholds}
                    representativePeaks={representativePeaks}
                    showAllDots={effectiveShowAllDots}
                  />
                )}
                activeDot={{ r: 6, stroke: '#fff', strokeWidth: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
